import {
    AlertCircle,
    Building2,
    Edit,
    Eye,
    MapPin,
    Plus,
    Search,
    Settings,
    Trash2,
    TrendingUp,
    Upload,
    Users
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import UnitBulkImport from '../../components/UnitBulkImport';
import api from '../../services/api';

const EMPTY_ARRAY = [];

const RMCBuildings = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [hasInitializedOwner, setHasInitializedOwner] = useState(false);
  const [selectedBuildingFilter, setSelectedBuildingFilter] = useState('');
  const [shouldSelectFirstBuilding, setShouldSelectFirstBuilding] = useState(true);
  const [buildingType, setBuildingType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedBuildingForBulkImport, setSelectedBuildingForBulkImport] = useState(null);

  // 건물 목록 가져오기
  const { data: buildingsData, isLoading } = useQuery(
    ['rmc-buildings', currentPage, searchTerm, selectedOwner, selectedBuildingFilter, buildingType],
    async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '20',
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (selectedOwner) params.append('property_owner_id', selectedOwner);
      if (selectedBuildingFilter) params.append('building_id', selectedBuildingFilter);
      if (buildingType) params.append('building_type', buildingType);
      
      const response = await api.get(`/rmc/buildings?${params}`);
      return response;
    }
  );

  const { data: buildingOptionsData } = useQuery(
    ['rmc-building-options', selectedOwner],
    async () => {
      if (!selectedOwner) return { buildings: [] };

      const params = new URLSearchParams({
        property_owner_id: selectedOwner,
        per_page: '1000',
      });

      const response = await api.get(`/rmc/buildings?${params}`);
      return response;
    },
    { enabled: !!selectedOwner }
  );

  const buildingOptions = buildingOptionsData?.buildings || EMPTY_ARRAY;

  // 임대사업자 목록 가져오기
  const fetchPropertyOwners = useCallback(async () => {
    try {
      const response = await api.get('/rmc/property-owners');
      console.log('임대사업자 목록 응답:', response);
      
      if (response && Array.isArray(response)) {
        setPropertyOwners(response);
        console.log('✅ 임대사업자 목록 설정 완료:', response.length, '개');
      } else {
        console.log('⚠️ 임대사업자 목록 데이터가 없거나 배열이 아닙니다');
        setPropertyOwners([]);
      }
    } catch (error) {
      console.error('임대사업자 목록 조회 오류:', error);
      setPropertyOwners([]);
    }
  }, []);

  useEffect(() => {
    fetchPropertyOwners();
  }, [fetchPropertyOwners]);

  useEffect(() => {
    if (hasInitializedOwner || selectedOwner || propertyOwners.length === 0) {
      return;
    }

    setSelectedOwner(String(propertyOwners[0].id));
    setHasInitializedOwner(true);
    setShouldSelectFirstBuilding(true);
    setCurrentPage(1);
  }, [hasInitializedOwner, propertyOwners, selectedOwner]);

  useEffect(() => {
    if (!selectedOwner || !buildingOptions.length) {
      return;
    }
    if (!shouldSelectFirstBuilding) {
      return;
    }

    const hasSelectedBuilding = buildingOptions.some(
      (building) => String(building.id) === String(selectedBuildingFilter)
    );
    if (!hasSelectedBuilding) {
      setSelectedBuildingFilter(String(buildingOptions[0].id));
      setCurrentPage(1);
    }
    setShouldSelectFirstBuilding(false);
  }, [buildingOptions, selectedBuildingFilter, selectedOwner, shouldSelectFirstBuilding]);

  // 건물 통계 가져오기
  const { data: statistics } = useQuery(
    'rmc-buildings-statistics',
    async () => {
      const response = await api.get('/rmc/buildings/statistics');
      return response;
    }
  );

  // 건물 삭제 뮤테이션
  const deleteBuildingMutation = useMutation(
    (buildingId) => api.delete(`/rmc/buildings/${buildingId}`),
    {
      onSuccess: () => {
        toast.success('건물이 성공적으로 삭제되었습니다.');
        queryClient.invalidateQueries('rmc-buildings');
        queryClient.invalidateQueries('rmc-buildings-statistics');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '건물 삭제에 실패했습니다.');
      }
    }
  );

  // 건물 삭제 확인
  const handleDeleteBuilding = (building) => {
    if (window.confirm(`"${building.name}" 건물을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.`)) {
      deleteBuildingMutation.mutate(building.id);
    }
  };

  // 검색 및 필터 핸들러
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedOwner(propertyOwners.length > 0 ? String(propertyOwners[0].id) : '');
    setHasInitializedOwner(true);
    setSelectedBuildingFilter('');
    setShouldSelectFirstBuilding(true);
    setBuildingType('');
    setCurrentPage(1);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const buildings = buildingsData?.buildings || EMPTY_ARRAY;
  const pagination = buildingsData?.pagination || {};
  const owners = propertyOwners;
  const stats = statistics || {};

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">건물 관리</h1>
          <p className="text-gray-600">소속 임대사업자들의 건물을 통합 관리합니다</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setShowStatistics(!showStatistics)}
            className="flex items-center whitespace-nowrap px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <TrendingUp className="w-4 h-4 mr-2 flex-shrink-0" />
            통계 보기
          </button>
          <button
            onClick={() => navigate('/rmc/buildings/add')}
            className="flex items-center whitespace-nowrap px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
            건물 등록
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      {showStatistics && stats.overview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Building2 className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 건물 수</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.total_buildings}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">총 세대 수</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.total_units}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <Settings className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">관리 임대사업자</p>
                <p className="text-2xl font-bold text-gray-900">{stats.overview.total_property_owners}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <AlertCircle className="w-8 h-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">평균 세대/건물</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.overview.total_buildings > 0 
                    ? Math.round(stats.overview.total_units / stats.overview.total_buildings)
                    : 0
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 검색 및 필터 */}
      <div className="bg-white p-6 rounded-lg shadow">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                임대사업자
              </label>
              <select
                value={selectedOwner}
                onChange={(e) => {
                  setSelectedOwner(e.target.value);
                  setHasInitializedOwner(true);
                  setSelectedBuildingFilter('');
                  setShouldSelectFirstBuilding(true);
                  handleFilterChange();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">전체 임대사업자</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                건물
              </label>
              <select
                value={selectedBuildingFilter}
                onChange={(e) => {
                  setSelectedBuildingFilter(e.target.value);
                  setShouldSelectFirstBuilding(false);
                  handleFilterChange();
                }}
                disabled={!selectedOwner}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
              >
                <option value="">{selectedOwner ? '전체 건물' : '임대사업자 먼저 선택'}</option>
                {buildingOptions.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                검색
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="건물명, 주소 검색"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                건물 유형
              </label>
              <select
                value={buildingType}
                onChange={(e) => {
                  setBuildingType(e.target.value);
                  handleFilterChange();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">전체</option>
                <option value="아파트">아파트</option>
                <option value="오피스텔">오피스텔</option>
                <option value="빌라">빌라</option>
                <option value="원룸">원룸</option>
                <option value="상가">상가</option>
              </select>
            </div>
            <div className="flex items-end space-x-2">
              <button
                type="submit"
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                검색
              </button>
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                초기화
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* 건물 목록 */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">건물 목록</h2>
        </div>
        
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">건물 목록을 불러오는 중...</p>
          </div>
        ) : buildings.length === 0 ? (
          <div className="p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">등록된 건물이 없습니다.</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200 md:hidden">
              {buildings.map((building) => (
                <article key={building.id} className="space-y-4 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-base font-semibold text-gray-900">
                        {building.name}
                      </h3>
                      <p className="mt-1 flex items-start text-sm text-gray-500">
                        <MapPin className="mr-1 mt-0.5 h-4 w-4 shrink-0" />
                        <span className="break-words">{building.address || '-'}</span>
                      </p>
                    </div>
                    <span className={`inline-flex shrink-0 rounded-full px-2 py-1 text-xs font-semibold ${
                      building.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {building.status === 'active' ? '활성' : '비활성'}
                    </span>
                  </div>

                  <dl className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <dt className="text-xs font-medium text-gray-500">임대사업자</dt>
                      <dd className="mt-1 font-medium text-gray-900">
                        {building.property_owner?.company_name || building.property_owner?.name || '알 수 없음'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">세대 현황</dt>
                      <dd className="mt-1 font-medium text-gray-900">총 {building.total_units}세대</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">건물 유형</dt>
                      <dd className="mt-1">
                        <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">
                          {building.building_type || '미분류'}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-medium text-gray-500">등록일</dt>
                      <dd className="mt-1 text-gray-900">
                        {building.created_at ? new Date(building.created_at).toLocaleDateString() : '-'}
                      </dd>
                    </div>
                  </dl>

                  <div className="grid grid-cols-5 gap-2 pt-1">
                    <button
                      onClick={() => navigate(`/rmc/buildings/${building.id}`)}
                      className="inline-flex min-h-[40px] items-center justify-center rounded-md border border-blue-200 text-blue-700"
                      title="상세 보기"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/rmc/buildings/${building.id}/edit`)}
                      className="inline-flex min-h-[40px] items-center justify-center rounded-md border border-indigo-200 text-indigo-700"
                      title="수정"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/rmc/buildings/${building.id}/units`)}
                      className="inline-flex min-h-[40px] items-center justify-center rounded-md border border-green-200 text-green-700"
                      title="세대 관리"
                    >
                      <Users className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedBuildingForBulkImport(building);
                        setShowBulkImport(true);
                      }}
                      className="inline-flex min-h-[40px] items-center justify-center rounded-md border border-purple-200 text-purple-700"
                      title="세대 일괄 등록"
                    >
                      <Upload className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteBuilding(building)}
                      className="inline-flex min-h-[40px] items-center justify-center rounded-md border border-red-200 text-red-700"
                      title="삭제"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      건물 정보
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      임대사업자
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      세대 현황
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      건물 유형
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      상태
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      등록일
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {buildings.map((building) => (
                    <tr key={building.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {building.name}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <MapPin className="w-3 h-3 mr-1" />
                            {building.address}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {building.property_owner?.name || '알 수 없음'}
                        </div>
                        {building.property_owner?.company_name && (
                          <div className="text-sm text-gray-500">
                            {building.property_owner.company_name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          총 {building.total_units}세대
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {building.building_type || '미분류'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          building.status === 'active' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {building.status === 'active' ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {building.created_at ? new Date(building.created_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/rmc/buildings/${building.id}`)}
                            className="text-blue-600 hover:text-blue-900"
                            title="상세 보기"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/rmc/buildings/${building.id}/edit`)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="수정"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/rmc/buildings/${building.id}/units`)}
                            className="text-green-600 hover:text-green-900"
                            title="세대 관리"
                          >
                            <Users className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedBuildingForBulkImport(building);
                              setShowBulkImport(true);
                            }}
                            className="text-purple-600 hover:text-purple-900"
                            title="세대 일괄 등록"
                          >
                            <Upload className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteBuilding(building)}
                            className="text-red-600 hover:text-red-900"
                            title="삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    총 {pagination.total}개 중 {((pagination.page - 1) * pagination.per_page) + 1}-
                    {Math.min(pagination.page * pagination.per_page, pagination.total)}개 표시
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      이전
                    </button>
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      const pageNum = Math.max(1, pagination.page - 2) + i;
                      if (pageNum > pagination.pages) return null;
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1 border rounded text-sm ${
                            pageNum === pagination.page
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.pages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      다음
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 세대 일괄 등록 모달 */}
      {showBulkImport && selectedBuildingForBulkImport && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  세대 일괄 등록 - {selectedBuildingForBulkImport.name}
                </h3>
                <button
                  onClick={() => {
                    setShowBulkImport(false);
                    setSelectedBuildingForBulkImport(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">닫기</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <UnitBulkImport
                buildingId={selectedBuildingForBulkImport.id}
                buildingName={selectedBuildingForBulkImport.name}
                onSuccess={() => {
                  console.log('🔍 onSuccess 콜백 실행');
                  console.log('🔍 selectedBuildingForBulkImport:', selectedBuildingForBulkImport);
                  
                  // 성공 시 관련된 모든 쿼리 새로고침
                  queryClient.invalidateQueries(['rmc-buildings']);
                  queryClient.invalidateQueries('rmc-buildings-statistics');
                  queryClient.invalidateQueries('all-units');
                  
                  // 세대 목록 쿼리 키 무효화 (RMCBuildingDetail에서 사용하는 키)
                  if (selectedBuildingForBulkImport?.id) {
                    queryClient.invalidateQueries(['rmc-building-units', selectedBuildingForBulkImport.id]);
                    queryClient.invalidateQueries(['buildings', selectedBuildingForBulkImport.id, 'units']);
                    queryClient.invalidateQueries(['rmc-building', selectedBuildingForBulkImport.id]);
                  }
                  
                  // 쿼리 무효화 후 데이터 다시 가져오기
                  queryClient.refetchQueries(['rmc-buildings', currentPage, searchTerm, selectedOwner, selectedBuildingFilter, buildingType]);
                  
                  // 성공 시 모달 닫기
                  console.log('🔍 모달 닫기 시작');
                  setTimeout(() => {
                    console.log('🔍 모달 닫기 실행');
                    setShowBulkImport(false);
                    setSelectedBuildingForBulkImport(null);
                  }, 500);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RMCBuildings;
